import React from 'react';
import { supabase } from '../supabaseClient';
import { toast } from 'react-toastify';

/**
 * Realtime Service for Order Updates
 * Provides WebSocket-based live updates for orders using Supabase Realtime
 */

class RealtimeService {
  constructor() {
    this.channel = null;
    this.subscribers = new Map();
    this.connectionStatus = 'disconnected';
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
  }

  /**
   * Subscribe to order updates
   * @param {string} subscriberId - Unique identifier for the subscriber
   * @param {Function} callback - Callback function to handle updates
   * @param {Object} options - Subscription options
   */
  subscribeToOrders(subscriberId, callback, options = {}) {
    const {
      showNotifications = true,
      events = ['INSERT', 'UPDATE', 'DELETE']
    } = options;

    // Store subscriber
    this.subscribers.set(subscriberId, { callback, options });

    // Initialize channel if not exists
    if (!this.channel) {
      this.initializeChannel(events);
    }

    return () => this.unsubscribe(subscriberId);
  }

  /**
   * Initialize Supabase Realtime channel
   */
  initializeChannel(events) {
    console.log('[Realtime] Initializing channel for order updates...');
    
    this.channel = supabase
      .channel('orders-realtime')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'orders'
      }, (payload) => {
        this.handleOrderChange(payload);
      })
      .subscribe((status) => {
        this.handleConnectionStatus(status);
      });
  }

  /**
   * Handle order change events
   */
  handleOrderChange(payload) {
    const { eventType, new: newRecord, old: oldRecord } = payload;

    console.log('[Realtime] Order change detected:', {
      eventType,
      orderId: newRecord?.id || oldRecord?.id,
      status: newRecord?.status,
      internalStatus: newRecord?.internal_status
    });

    // Notify all subscribers
    this.subscribers.forEach(({ callback, options }) => {
      if (options.events.includes(eventType)) {
        callback({ eventType, newRecord, oldRecord });
      }
    });

    // Show toast notifications for important events
    this.handleNotifications(eventType, newRecord, oldRecord);
  }

  /**
   * Handle connection status changes
   */
  handleConnectionStatus(status) {
    const previousStatus = this.connectionStatus;
    this.connectionStatus = status;

    console.log('[Realtime] Connection status:', status);

    if (status === 'SUBSCRIBED') {
      this.reconnectAttempts = 0;
      if (previousStatus === 'CHANNEL_ERROR' || previousStatus === 'TIMED_OUT') {
        toast.success('Reconnected to live updates', {
          position: 'bottom-right',
          autoClose: 3000
        });
      }
    } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
      this.handleReconnection();
    } else if (status === 'CLOSED') {
      console.log('[Realtime] Connection closed');
    }
  }

  /**
   * Handle reconnection attempts
   */
  handleReconnection() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
      
      console.log(`[Realtime] Attempting reconnection (${this.reconnectAttempts}/${this.maxReconnectAttempts}) in ${delay}ms`);
      
      setTimeout(() => {
        if (this.channel) {
          this.channel.unsubscribe();
          this.channel = null;
        }
        if (this.subscribers.size > 0) {
          this.initializeChannel(['INSERT', 'UPDATE', 'DELETE']);
        }
      }, delay);
    } else {
      toast.error('Lost connection to live updates. Please refresh the page.', {
        position: 'bottom-right',
        autoClose: false
      });
    }
  }

  /**
   * Handle toast notifications for order events
   */
  handleNotifications(eventType, newRecord, oldRecord) {
    if (eventType === 'INSERT') {
      // New order created
      if (newRecord?.internal_status === 'pending_approval') {
        toast.info(`New order #${newRecord.order_number} requires approval`, {
          position: 'top-right',
          autoClose: 5000,
          onClick: () => {
            // Navigate to approvals page
            if (window.location.pathname.includes('/admin')) {
              window.location.href = '/admin/approvals';
            } else if (window.location.pathname.includes('/staff')) {
              window.location.href = '/staff-area/approvals';
            }
          }
        });
      }
    } else if (eventType === 'UPDATE') {
      // Order status changed
      const statusChanged = oldRecord?.status !== newRecord?.status;
      const internalStatusChanged = oldRecord?.internal_status !== newRecord?.internal_status;
      
      if (internalStatusChanged && newRecord?.internal_status === 'pending_approval') {
        toast.info(`Order #${newRecord.order_number} now requires approval`, {
          position: 'top-right',
          autoClose: 5000
        });
      } else if (statusChanged) {
        // Silent update - let dashboard components handle display
        console.log(`[Realtime] Order #${newRecord.order_number} status: ${oldRecord?.status} → ${newRecord?.status}`);
      }
    }
  }

  /**
   * Unsubscribe a specific subscriber
   */
  unsubscribe(subscriberId) {
    this.subscribers.delete(subscriberId);
    
    // If no more subscribers, cleanup channel
    if (this.subscribers.size === 0 && this.channel) {
      console.log('[Realtime] No more subscribers, closing channel');
      this.channel.unsubscribe();
      this.channel = null;
      this.connectionStatus = 'disconnected';
    }
  }

  /**
   * Get current connection status
   */
  getConnectionStatus() {
    return this.connectionStatus;
  }

  /**
   * Check if connected
   */
  isConnected() {
    return this.connectionStatus === 'SUBSCRIBED';
  }

  /**
   * Manually disconnect
   */
  disconnect() {
    if (this.channel) {
      this.channel.unsubscribe();
      this.channel = null;
    }
    this.subscribers.clear();
    this.connectionStatus = 'disconnected';
    console.log('[Realtime] Manually disconnected');
  }
}

// Export singleton instance
export const realtimeService = new RealtimeService();

/**
 * React Hook for using realtime order updates
 */
export const useOrderUpdates = (options = {}) => {
  const [connectionStatus, setConnectionStatus] = React.useState('disconnected');
  const subscriberIdRef = React.useRef(`subscriber-${Date.now()}-${Math.random()}`);

  React.useEffect(() => {
    const callback = (payload) => {
      if (options.onUpdate) {
        options.onUpdate(payload);
      }
    };

    // Subscribe to updates
    const unsubscribe = realtimeService.subscribeToOrders(
      subscriberIdRef.current,
      callback,
      options
    );

    // Monitor connection status
    const statusInterval = setInterval(() => {
      setConnectionStatus(realtimeService.getConnectionStatus());
    }, 1000);

    return () => {
      unsubscribe();
      clearInterval(statusInterval);
    };
  }, []);

  return {
    connectionStatus,
    isConnected: realtimeService.isConnected()
  };
};

export default realtimeService;
